<template>
  <DashboardLayout :title="$t('profile.friend_codes.title')">
    <div class="mx-auto w-full max-w-4xl flex flex-col gap-6">
      <!-- Page header -->
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-xl bg-primary-600/15 border border-primary-800 flex items-center justify-center flex-shrink-0">
          <GiftIcon class="w-6 h-6 text-primary-300" />
        </div>
        <div class="flex-1 min-w-0">
          <h1 class="text-text-primary text-[28px] font-medium leading-tight">{{ $t('profile.friend_codes.title') }}</h1>
          <p class="text-sm text-text-secondary mt-0.5">
            {{ $t('profile.friend_codes.intro') }}
          </p>
        </div>
        <router-link
          :to="{ name: 'Redeem' }"
          class="flex-shrink-0 text-sm font-medium text-primary-300 rounded-md px-3 py-2 hover:bg-white/5 duration-200"
        >
          {{ $t('profile.friend_codes.redeem_link') }}
        </router-link>
      </div>

      <section class="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
        <header class="flex items-center gap-4 px-6 py-4 border-b border-gray-800">
          <div class="flex-1 min-w-0">
            <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">
              {{ $t('profile.friend_codes.your_codes') }}<template v-if="!loading"> ({{ codes.length }})</template>
            </p>
            <p class="text-sm text-text-secondary mt-1">
              {{ $t('profile.friend_codes.subscribers_note') }}
            </p>
          </div>
          <button
            type="button"
            :disabled="loading"
            :title="$t('profile.friend_codes.refresh')"
            class="w-9 h-9 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white duration-200 flex-shrink-0"
            @click="fetchCodes"
          >
            <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': loading }" />
          </button>
        </header>

        <!-- These are gifts, not something you can use yourself -->
        <div
          v-if="!loading && !loadError && codes.length"
          class="flex items-start gap-2.5 px-6 py-3 bg-primary-600/10 border-b border-gray-800"
        >
          <UsersIcon class="w-4 h-4 text-primary-300 flex-shrink-0 mt-px" />
          <p class="text-xs text-primary-200">
            {{ $t('profile.friend_codes.share_only_note') }}
          </p>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="divide-y divide-gray-800">
          <div v-for="row in 3" :key="row" class="px-6 py-5 flex items-center gap-3 animate-pulse">
            <div class="h-6 w-28 rounded bg-gray-800" />
            <div class="h-5 w-20 rounded bg-gray-800" />
            <div class="flex-1" />
            <div class="h-9 w-24 rounded-lg bg-gray-800" />
          </div>
        </div>

        <!-- Could not load -->
        <div v-else-if="loadError" class="px-6 py-12 flex flex-col items-center text-center gap-3">
          <ExclamationTriangleIcon class="w-8 h-8 text-danger-400" />
          <p class="text-sm text-text-secondary">{{ loadError }}</p>
          <button
            type="button"
            class="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-text-primary text-sm font-semibold duration-200"
            @click="fetchCodes"
          >
            {{ $t('profile.friend_codes.try_again') }}
          </button>
        </div>

        <!-- Empty -->
        <div v-else-if="!codes.length" class="px-6 py-14 flex flex-col items-center text-center gap-2">
          <TicketIcon class="w-10 h-10 text-gray-700" />
          <p class="text-text-primary font-medium">{{ $t('profile.friend_codes.empty_title') }}</p>
          <p class="text-sm text-text-secondary max-w-sm">
            {{ $t('profile.friend_codes.empty_text') }}
          </p>
          <router-link
            to="/upgrade"
            class="mt-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold duration-200"
          >
            {{ $t('profile.friend_codes.see_plans') }}
          </router-link>
        </div>

        <!-- Codes -->
        <ul v-else class="divide-y divide-gray-800">
          <li
            v-for="code in codes"
            :key="code.code"
            class="px-6 py-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between hover:bg-white/[0.02] duration-200"
          >
            <div class="min-w-0 flex flex-col gap-2">
              <div class="flex items-center gap-3 flex-wrap">
                <span
                  class="font-mono text-lg font-bold tracking-tight text-white"
                  :class="{ 'blur-sm hover:blur-none duration-200': user?.streamer_mode }"
                >
                  {{ code.code }}
                </span>
                <span
                  class="px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider"
                  :class="tierBadge(code).classes"
                >
                  {{ tierBadge(code).label }}
                </span>
                <span
                  class="px-2 py-0.5 rounded-md border border-gray-700 bg-gray-800 text-[10px] font-bold uppercase tracking-wider text-gray-300"
                >
                  {{ codeType(code) }}
                </span>
              </div>

              <div class="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                <span class="flex items-center gap-1.5">
                  <ClockIcon class="w-3.5 h-3.5" />
                  {{ $t('profile.friend_codes.gives_days', { n: code.duration_in_days }) }}
                </span>
                <span class="text-gray-600">•</span>
                <span :class="{ 'text-warning-400': expiringSoon(code) }">{{ expiryLabel(code) }}</span>
              </div>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                :title="$t('profile.friend_codes.copy_code')"
                class="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold duration-200"
                @click="copy(code.code, `${code.code}:code`)"
              >
                <CheckIcon v-if="copied === `${code.code}:code`" class="w-4 h-4" />
                <ClipboardDocumentIcon v-else class="w-4 h-4" />
                {{ copied === `${code.code}:code` ? $t('profile.friend_codes.copied') : $t('profile.friend_codes.copy_code') }}
              </button>

              <button
                type="button"
                :title="$t('profile.friend_codes.copy_link_title')"
                class="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm font-medium duration-200"
                @click="copy(redeemLink(code), `${code.code}:link`)"
              >
                <CheckIcon v-if="copied === `${code.code}:link`" class="w-4 h-4 text-green-400" />
                <LinkIcon v-else class="w-4 h-4" />
                {{ copied === `${code.code}:link` ? $t('profile.friend_codes.copied') : $t('profile.friend_codes.copy_link') }}
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import moment from 'moment';
import {
  GiftIcon,
  UsersIcon,
  TicketIcon,
  ClockIcon,
  LinkIcon,
  CheckIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/solid';
import DashboardLayout from '../components/layouts/DashboardLayout.vue';

const auth = useAuth();
const { t } = useI18n();

const user = ref(auth.user());
const codes = ref([]);
const loading = ref(true);
const loadError = ref(null);
const copied = ref(null);
let copiedTimer = null;

// Static class strings so Tailwind's JIT keeps them. Tiers match XsollaSubscription::PLANS.
const TIERS = {
  player: { label: 'profile.friend_codes.tier_player', classes: 'bg-indigo-900/50 border-indigo-800/60 text-indigo-300' },
  competitor: { label: 'profile.friend_codes.tier_competitor', classes: 'bg-blue-900/50 border-blue-800/60 text-blue-300' },
  teams: { label: 'profile.friend_codes.tier_team', classes: 'bg-green-900/50 border-green-800/60 text-green-300' },
  pro: { label: 'profile.friend_codes.tier_pro', classes: 'bg-yellow-900/50 border-yellow-800/60 text-yellow-300' },
};
const FALLBACK_TIER = { label: 'profile.friend_codes.type_trial', classes: 'bg-gray-800 border-gray-700 text-gray-300' };

function tierBadge(code) {
  const tier = TIERS[code?.tier] || FALLBACK_TIER;
  return { label: t(tier.label), classes: tier.classes };
}

function codeType(code) {
  return code.ignore_limit ? t('profile.friend_codes.type_access') : t('profile.friend_codes.type_trial');
}

function expiryLabel(code) {
  if (!code.valid_until) return t('profile.friend_codes.never_expires');
  const until = moment(code.valid_until);
  return t('profile.friend_codes.expires', { relative: until.fromNow(), date: until.format('LL') });
}

function expiringSoon(code) {
  return !!code.valid_until && moment(code.valid_until).diff(moment(), 'days') <= 7;
}

// A friend who opens this lands on /redeem with the code already filled in
function redeemLink(code) {
  return `${window.location.origin}/redeem?code=${code.code}`;
}

async function copy(value, key) {
  try {
    await navigator.clipboard.writeText(value);
    copied.value = key;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => (copied.value = null), 1500);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
  }
}

async function fetchCodes() {
  loading.value = true;
  loadError.value = null;
  try {
    const { data } = await axios.get('/trial_codes');
    codes.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Failed to fetch trial codes:', error);
    loadError.value = t('profile.friend_codes.load_error');
  } finally {
    loading.value = false;
  }
}

useHead({
  title: () => t('profile.friend_codes.page_title'),
});

onMounted(fetchCodes);

onUnmounted(() => {
  clearTimeout(copiedTimer);
});
</script>
